import { useEffect, useRef } from 'react'
import { usePersonalizationWidgetData } from '@vp/design-personalization-widget'
import { useIdentity } from '@vp/ubik-context'
import { dpcsLoadingUpdate } from '~/app/store/designPersonalization/actions'
import { useDispatch } from 'react-redux'
import { contentUpdate } from '~/app/store/content'
import { ThunkDispatch } from '~/app/store/interface'
import {
  designPersonalizationContextIdUpdate,
  designPersonalizationWidgetHasImageUpdate,
  designPersonalizationWidgetHasColorUpdate,
  colorVariationDPCsUpdate,
} from '~/app/store/designPersonalization/actions'

export const ReduxBridge = (): null => {
  const reduxDispatch = useDispatch<ThunkDispatch>()
  const { auth } = useIdentity()
  const { designPersonalizationContextId, designPersonalizationHasImage, designPersonalizationHasColor, colorVariationDPCs, isPersonalizationWidgetUpdating } =
    usePersonalizationWidgetData()

  const prevHasImages = useRef<boolean>(designPersonalizationHasImage)
  const prevHasColor = useRef<boolean>(designPersonalizationHasColor)
  const prevColorVariationDPCsJson = useRef<string>(JSON.stringify(colorVariationDPCs))
  const prevDpcId = useRef<string | undefined>(designPersonalizationContextId)
  const prevIsUpdating = useRef<boolean>(isPersonalizationWidgetUpdating)

  // The first complete true → false cycle of isPersonalizationWidgetUpdating is always
  // widget initialization — not a user-triggered change. We skip dpcsLoading for that
  // cycle to avoid showing tile spinners before the user has interacted with anything
  // (fixes stuck spinner on direct-QV page load).
  const isWidgetInitializing = useRef<boolean>(true)

  // Show loading immediately when widget starts updating (skip initial load)
  useEffect(() => {
    if (isPersonalizationWidgetUpdating && !isWidgetInitializing.current) {
      reduxDispatch(dpcsLoadingUpdate(true))
    }
  }, [isPersonalizationWidgetUpdating, reduxDispatch])

  // Multiple deps — only dispatch the field(s) that actually changed to avoid
  // cascading Redux re-renders for unchanged personalization state.
  useEffect(() => {
    const dpcIdChanged = prevDpcId.current !== designPersonalizationContextId
    const hasImageChanged = prevHasImages.current !== designPersonalizationHasImage
    const hasColorChanged = prevHasColor.current !== designPersonalizationHasColor
    const nextColorVariationDPCsJson = JSON.stringify(colorVariationDPCs)
    const colorVariationDPCsChanged = prevColorVariationDPCsJson.current !== nextColorVariationDPCsJson

    if (dpcIdChanged) {
      reduxDispatch(designPersonalizationContextIdUpdate(designPersonalizationContextId))
      prevDpcId.current = designPersonalizationContextId
    }
    if (hasImageChanged) {
      reduxDispatch(designPersonalizationWidgetHasImageUpdate(designPersonalizationHasImage))
      prevHasImages.current = designPersonalizationHasImage
    }
    if (hasColorChanged) {
      reduxDispatch(designPersonalizationWidgetHasColorUpdate(designPersonalizationHasColor))
      prevHasColor.current = designPersonalizationHasColor
    }
    if (colorVariationDPCsChanged) {
      reduxDispatch(colorVariationDPCsUpdate(colorVariationDPCs))
      prevColorVariationDPCsJson.current = nextColorVariationDPCsJson
    }

    // Fallback: if relevant fields changed while the widget is NOT in an updating
    // cycle (e.g. toggle on/off which doesn't cycle isPersonalizationWidgetUpdating),
    // trigger a content re-fetch here. When the widget does cycle the flag, Effect 3
    // handles the re-fetch instead — so this avoids duplicate calls.
    const hasRelevantChange = dpcIdChanged || hasImageChanged || hasColorChanged || colorVariationDPCsChanged
    if (hasRelevantChange && !isPersonalizationWidgetUpdating && !isWidgetInitializing.current) {
      reduxDispatch(dpcsLoadingUpdate(true))
      reduxDispatch(contentUpdate({ shouldUpdateUrl: false })).finally(() => {
        reduxDispatch(dpcsLoadingUpdate(false))
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, designPersonalizationContextId, designPersonalizationHasImage, designPersonalizationHasColor, colorVariationDPCs])

  // Trigger content re-fetch when widget finishes updating (true → false transition).
  // By this point all field dispatches above have already settled in Redux,
  // so the content query picks up fully consistent state.
  useEffect(() => {
    const wasUpdating = prevIsUpdating.current
    prevIsUpdating.current = isPersonalizationWidgetUpdating

    if (wasUpdating && !isPersonalizationWidgetUpdating) {
      if (isWidgetInitializing.current) {
        // Widget finished its initial load — fetch personalized content silently
        // without triggering tile spinners (user hasn't interacted yet).
        isWidgetInitializing.current = false

        // The field-sync effect (above) may skip dispatch on mount when its
        // prev-refs are pre-seeded with the same values the hook already provides.
        // Flush all fields into Redux synchronously so the content thunk reads
        // consistent state when it calls getState().
        reduxDispatch(designPersonalizationContextIdUpdate(designPersonalizationContextId))
        reduxDispatch(designPersonalizationWidgetHasImageUpdate(designPersonalizationHasImage))
        reduxDispatch(designPersonalizationWidgetHasColorUpdate(designPersonalizationHasColor))
        reduxDispatch(colorVariationDPCsUpdate(colorVariationDPCs))

        reduxDispatch(contentUpdate({ shouldUpdateUrl: false }))
      } else {
        // User-triggered change — show spinners while re-fetching
        reduxDispatch(dpcsLoadingUpdate(true))
        reduxDispatch(contentUpdate({ shouldUpdateUrl: false })).finally(() => {
          reduxDispatch(dpcsLoadingUpdate(false))
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPersonalizationWidgetUpdating, reduxDispatch])

  return null
}
